import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faAngleLeft, faAngleRight, faClose } from "@fortawesome/free-solid-svg-icons"
import { type IconProp } from "@fortawesome/fontawesome-svg-core"
import GetSpace from "../../utils/space"
import { EmployeeCard } from "../EmployeeCard/EmployeeCard"
import { useEmployerLogged } from "../../context/useEmployeeContext"
import { useMenuMobileContext } from "../../context/useMenuMobileContext"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { FloatButton } from "../FloatButton/FloatButton"
import { PermissionsType } from "../../Enum/PermissionsType"
import type { SystemModuleType } from "../../Enum/SystemModuleType"
import { getDateRangeSearch } from "../../utils/dateRangeSearchParams"
import { useCompanyModulesStatus } from "../../context/useCompanyModulesStatusContext"
import { canAccessRestrictedPage } from "../../utils/employeeAccess"

export class AuxNavBar {
  title: string

  icon: IconProp

  path: string

  descriptionKey: string

  mobileDescriptionKey: string

  params?: Record<string, unknown>

  index: number

  permission?: PermissionsType

  module?: SystemModuleType

  constructor(
    index: number,
    title: string,
    icon: IconProp,
    path: string,
    descriptionKey: string,
    mobileDescriptionKey: string,
    permission?: PermissionsType,
    module?: SystemModuleType,
    params?: Record<string, unknown>,
  ) {
    this.title = title

    this.index = index

    this.icon = icon

    this.path = path

    this.descriptionKey = descriptionKey

    this.mobileDescriptionKey = mobileDescriptionKey

    this.permission = permission

    this.module = module

    this.params = params
  }
}

export class ObjectNavbarMenu {
  title: string

  iconMenu: IconProp

  descriptionKey: string

  listMenus: AuxNavBar[] = []

  expanded: boolean = false

  constructor(title: string, iconMenu: IconProp, descriptionKey: string) {
    this.title = title

    this.iconMenu = iconMenu

    this.descriptionKey = descriptionKey
  }

  addMenu(
    index: number,

    title: string,

    icon: IconProp,

    path: string,

    descriptionKey: string,

    mobileDescriptionKey: string,

    permission?: PermissionsType,

    module?: SystemModuleType,

    params?: Record<string, unknown>,
  ) {
    this.expanded = true

    this.listMenus.push(
      new AuxNavBar(index, title, icon, path, descriptionKey, mobileDescriptionKey, permission, module, params),
    )
  }
}

const NAVBAR_COLLAPSED_STORAGE_KEY = "store_navbar_collapsed"

const SIDEBAR_WIDTH_CSS_VAR = "--store-sidebar-width"

const EXPANDED_SIDEBAR_WIDTH = "260px"

const COLLAPSED_SIDEBAR_WIDTH = "72px"

interface NavBarGlobalProps {
  isPublicLink: boolean

  listObjectNavBarMenu: ObjectNavbarMenu[]

  onMenuClick: (index: number) => void

  externalButton?: {
    title: string
    icon: IconProp
    url: string
    descriptionKey: string
  }
}

export const NavBarGlobal: React.FC<NavBarGlobalProps> = ({
  isPublicLink,
  listObjectNavBarMenu,
  onMenuClick,
  externalButton,
}) => {
  const location = useLocation()

  const navigate = useNavigate()

  const { t } = useTranslation("frontend")

  const { userEmployee } = useEmployerLogged()

  const { isModuleEnabled } = useCompanyModulesStatus()

  const { enableMenu, isNotMobile, handleForceCloseMenu } = useMenuMobileContext()

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => localStorage.getItem(NAVBAR_COLLAPSED_STORAGE_KEY) === "1",
  )

  const isCollapsedOnDesktop = isNotMobile && isSidebarCollapsed

  const handleToggleSidebarCollapse = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(NAVBAR_COLLAPSED_STORAGE_KEY, next ? "1" : "0")
      return next
    })
  }

  useEffect(() => {
    document.documentElement.style.setProperty(
      SIDEBAR_WIDTH_CSS_VAR,
      isCollapsedOnDesktop ? COLLAPSED_SIDEBAR_WIDTH : EXPANDED_SIDEBAR_WIDTH,
    )
  }, [isCollapsedOnDesktop])

  const navbarContainerRef = useRef<HTMLDivElement | null>(null)

  const activeLinkRef = useRef<HTMLDivElement | null>(null)

  const canView = useCallback((permission?: PermissionsType, moduleCode?: SystemModuleType) => {
    if (isPublicLink) return true

    return canAccessRestrictedPage(permission, moduleCode, userEmployee?.permissions, isModuleEnabled)
  }, [isModuleEnabled, isPublicLink, userEmployee?.permissions])

  const filteredMenus = useMemo(() => {
    return listObjectNavBarMenu
      .map((menu) => {
        const filteredSubMenus = menu.listMenus.filter((sub) => canView(sub.permission, sub.module))

        if (filteredSubMenus.length === 0) return null

        const newMenu = new ObjectNavbarMenu(menu.title, menu.iconMenu, menu.descriptionKey)
        newMenu.listMenus = filteredSubMenus
        newMenu.expanded = menu.listMenus.some((e) => e.path === location.pathname)

        return newMenu
      })
      .filter(Boolean) as ObjectNavbarMenu[]
  }, [canView, listObjectNavBarMenu, location.pathname])

  const [menus, setMenus] = useState<ObjectNavbarMenu[]>(filteredMenus)

  useEffect(() => {
    setMenus(filteredMenus)
  }, [filteredMenus])

  useEffect(() => {
    if (!enableMenu || !activeLinkRef.current || !navbarContainerRef.current) {
      return
    }

    const animationFrame = window.requestAnimationFrame(() => {
      activeLinkRef.current?.scrollIntoView({
        behavior: isNotMobile ? "auto" : "smooth",
        block: "center",
        inline: "nearest",
      })
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [enableMenu, isNotMobile, location.pathname, menus])

  const handleNavigate = (item: AuxNavBar) => {
    onMenuClick(item.index)

    navigate(`${item.path}${getDateRangeSearch(location.search)}`, { state: item.params })
  }

  const handleOpenExternalButton = () => {
    if (!externalButton?.url) {
      return
    }

    if (!isNotMobile) {
      handleForceCloseMenu()
    }

    window.open(externalButton.url, "_blank", "noopener,noreferrer")
  }

  const toggleExpansion = (index: number) => {
    const updatedMenus = [...menus]

    updatedMenus[index].expanded = !updatedMenus[index].expanded

    setMenus(updatedMenus)
  }

  return (
    <>
      {enableMenu && (
        <div
          ref={navbarContainerRef}
          className="fixed left-0 top-[60px] z-[960] block h-[calc(100dvh-60px)] w-full overflow-y-auto overflow-x-hidden scrollbar-thin bg-white shadow-[1px_1px_1px_rgba(0,0,0,0.05)] transition-[width] duration-200 md:z-0 md:flex md:w-[var(--store-sidebar-width,260px)] md:flex-col md:justify-between"
        >
          <div>
            {isNotMobile ? (
              <div
                className={`flex items-center gap-2 border-b border-slate-100 px-2 py-2 ${
                  isCollapsedOnDesktop ? "justify-center" : ""
                }`}
              >
                {userEmployee && !isCollapsedOnDesktop && (
                  <div className="min-w-0 flex-1">
                    <EmployeeCard employee={userEmployee} onEventClick={() => {}} />
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleToggleSidebarCollapse}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-md shadow-orange-500/30 transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-orange-500/40 active:scale-95"
                  title={t(isSidebarCollapsed ? "navbar.expand_sidebar" : "navbar.collapse_sidebar")}
                  data-interaction-description-key="interactionDescriptions:components.navbar.navbar.button.handleToggleSidebarCollapse"
                >
                  <FontAwesomeIcon icon={isSidebarCollapsed ? faAngleRight : faAngleLeft} />
                </button>
              </div>
            ) : (
              userEmployee && <EmployeeCard employee={userEmployee} onEventClick={handleForceCloseMenu} />
            )}

            {externalButton && !isCollapsedOnDesktop && (
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                <button
                  type="button"
                  onClick={handleOpenExternalButton}
                  className="mx-2 my-2 flex w-[calc(100%-16px)] items-center rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-bold text-slate-900 transition-all duration-200 hover:scale-[1.02] hover:bg-orange-100"
                  data-interaction-description-key={externalButton.descriptionKey}
                >
                  <FontAwesomeIcon icon={externalButton.icon} className="text-slate-900" />

                  <GetSpace left={15} />

                  <span>{externalButton.title}</span>
                </button>
              </Animate>
            )}

            <GetSpace top={10} />

            {isCollapsedOnDesktop ? (
              <nav>
                <ul className="flex flex-col items-center gap-1 px-2">
                  {menus
                    .flatMap((menu) => menu.listMenus)
                    .map((item) => {
                      const isActive = location.pathname === item.path

                      return (
                        <li key={item.title} className="flex w-full justify-center">
                          <div
                            ref={isActive ? activeLinkRef : undefined}
                            onClick={() => handleNavigate(item)}
                            title={item.title}
                            data-interaction-description-key={item.descriptionKey}
                            className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-sm transition-all duration-200 ${
                              isActive
                                ? "bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-md shadow-orange-500/30"
                                : "text-slate-600 hover:bg-orange-50 hover:text-orange-600"
                            }`}
                          >
                            <FontAwesomeIcon icon={item.icon} />
                          </div>
                        </li>
                      )
                    })}
                </ul>
              </nav>
            ) : (
              menus.map((item, index) => (
                <div key={index}>
                  <Animate type={AnimationTypes.fadeInTraslate} duration={`${1 + 0.3 * index}s`}>
                    {getTitle(item, () => toggleExpansion(index))}
                  </Animate>

                  <nav>
                    <ul className="flex flex-col">
                      {item.expanded &&
                        item.listMenus.map((subMenu, subIndex) => (
                          <Animate
                            key={subIndex}
                            type={AnimationTypes.fadeInTraslate}
                            duration={`${1 + 0.1 * subIndex}s`}
                          >
                            <div className="flex cursor-pointer">
                              <svg className="h-10 w-[10%] pl-[26px]">
                                <line x1="0" y1="0" x2="0" y2="100" stroke="black" strokeWidth={3} />
                              </svg>

                              {getLi(
                                () => {
                                  if (!isNotMobile) {
                                    handleForceCloseMenu()
                                  }

                                  handleNavigate(subMenu)
                                },
                                subMenu,
                                location.pathname === subMenu.path,
                                location.pathname === subMenu.path ? activeLinkRef : undefined,
                              )}
                            </div>
                          </Animate>
                        ))}
                    </ul>
                  </nav>
                </div>
              ))
            )}
            <GetSpace top={25} />
          </div>

          {!isNotMobile && (
            <FloatButton
              icon={faClose}
              onClick={handleForceCloseMenu}
              color="#000000ff"
              iconColor="white"
              bottom="5px"
              right="5px"
              iconSize="2x"
              size={50}
              animationDuration="1.5s"
              animationTypes={AnimationTypes.slideUpIn}
              descriptionKey="interactionDescriptions:components.navbar.navbar.button.handleForceCloseMenu"
            />
          )}
        </div>
      )}
    </>
  )
}

function getTitle(item: ObjectNavbarMenu, toggleExpansion: () => void) {
  return (
    <div
      onClick={toggleExpansion}
      className="flex h-[55px] w-full cursor-pointer items-center justify-between px-4 transition-transform duration-200 hover:scale-[1.02]"
      data-interaction-description-key={item.descriptionKey}
    >
      <div className="flex items-center text-black">
        <FontAwesomeIcon className="mr-4 h-5 w-5" icon={item.iconMenu} />

        <h2 className="text-[15px] font-bold text-slate-900">{item.title}</h2>
      </div>

      <FontAwesomeIcon className="h-5 w-5 text-black" icon={item.expanded ? faAngleDown : faAngleRight} />
    </div>
  )
}

function getLi(handleNavigate: () => void, item: AuxNavBar, isActive: boolean, activeRef?: React.Ref<HTMLDivElement>) {
  return (
    <li key={item.title} className="flex w-full">
      <div
        ref={activeRef}
        onClick={handleNavigate}
        data-interaction-description-key={item.descriptionKey}
        className={`flex w-full items-center py-2 pl-[25px] text-[0.9rem] font-semibold transition-all duration-200 ${
          isActive ? "scale-105 text-orange-600" : "text-slate-600 hover:scale-105 hover:text-slate-900"
        }`}
      >
        <FontAwesomeIcon icon={item.icon} className={isActive ? "text-orange-600" : "text-slate-900"} />

        <GetSpace left={15} />

        <span>{item.title}</span>
      </div>
    </li>
  )
}
